import { errorDetails, isAssistantRequestThrottle, isCredentialSupersededError, isUnavailableError } from "./aiAssistantState";

const activeTurnTags = ["sending", "renewing", "applying", "resyncing"];
const idleTurn = () => ({ tag: "idle", error: null });
const emptyDaily = () => ({ remaining: null, limit: null, resetAt: null });
const idleHistory = (epoch = 0, page = {}) => ({
  tag: "idle", epoch, hasMore: Boolean(page.hasMore), beforeSeq: page.beforeSeq ?? null
});

// The retry comparison base is detached from form props and remains read-only during fact application.
export function createTurnEnvelope(request, composerText) {
  const captured = JSON.parse(JSON.stringify(request));
  const freeze = (value) => {
    if (value && typeof value === "object") {
      Object.values(value).forEach(freeze);
      Object.freeze(value);
    }
    return value;
  };
  return freeze({ request: captured, turnContext: { dealState: captured.dealState }, composerText });
}

export function createAssistantLifecycle(dealId = null, epoch = 0) {
  return {
    context: { dealId, epoch },
    session: { tag: dealId ? "dormant" : "noDeal" },
    history: idleHistory(),
    turn: idleTurn(),
    readiness: { knownReadyForAnalysis: null, knownDefaultsConfirmed: null },
    messages: [],
    daily: emptyDaily(),
    throttle: null,
    operationSequence: 0,
    snapshotVersion: 0,
    historyAppliedKey: null
  };
}

export function selectAssistantPanelState(state) {
  const session = state.session;
  return {
    session: session.value || null,
    messages: state.messages,
    daily: state.daily,
    throttle: state.throttle,
    isSending: activeTurnTags.includes(state.turn.tag),
    isOpening: session.tag === "opening" || state.turn.tag === "resyncing",
    unavailable: session.tag === "blocked",
    credentialSuperseded: session.tag === "superseded",
    error: session.error || state.turn.error,
    isLoadingHistory: state.history.tag === "loading",
    historyHasMore: state.history.hasMore,
    historyBeforeSeq: state.history.beforeSeq
  };
}

export function selectOpeningOperation(state) {
  return state.session.tag === "opening" ? state.session.operation : null;
}

export function selectHistoryOperation(state) {
  return state.history.tag === "loading" ? state.history.operation : null;
}

export function selectTurnOperation(state) {
  return activeTurnTags.includes(state.turn.tag) ? state.turn.operation : null;
}

function matchesContext(state, event) {
  return event.epoch === state.context.epoch;
}

function matchesOperation(operation, event) {
  return Boolean(operation) && operation.key === event.key && operation.epoch === event.epoch;
}

function invalidateHistory(state) {
  return { ...state, history: idleHistory(state.history.epoch + 1, state.history), historyAppliedKey: null };
}

function validSession(session) {
  return typeof session?.sessionIdRef === "string" && Boolean(session.sessionIdRef.trim())
    && typeof session.conversationId === "string" && Boolean(session.conversationId.trim())
    && session.state !== null && typeof session.state === "object" && !Array.isArray(session.state);
}

const invalidSessionError = { code: "INVALID_SESSION_RESPONSE" };

function hasRetryableTurnThrottle(state) {
  return state.turn.tag === "retryable" && isAssistantRequestThrottle(state.turn.error);
}

function replaceSnapshot(state, event) {
  return {
    ...invalidateHistory(state),
    session: { tag: "connected", value: event.session },
    history: idleHistory(state.history.epoch + 1, event.history),
    messages: event.messages || [],
    turn: idleTurn(),
    daily: event.daily || state.daily,
    snapshotVersion: state.snapshotVersion + 1
  };
}

function recordThrottle(state, error, now) {
  const details = errorDetails(error);
  return isAssistantRequestThrottle(details) ? { ...state, throttle: {
    code: details.code, until: (Number.isFinite(now) ? now : 0) + details.retryAfterSeconds * 1000, expired: false
  } } : state;
}

function isCoolingDown(state, now) {
  return Boolean(state.throttle && state.throttle.until > (now ?? 0));
}

function sessionFailure(state, event, source) {
  const superseded = event.error?.code === "AUTH_SESSION_SUPERSEDED";
  return recordThrottle({
    ...invalidateHistory(state),
    session: { tag: isAssistantRequestThrottle(event.error) ? "throttled" : superseded ? "superseded" : "blocked", source, value: state.session.value || null, error: event.error },
    turn: idleTurn(),
    daily: event.daily || state.daily,
    messages: event.removeMessage ? state.messages.filter((item) => item !== event.removeMessage) : state.messages
  }, event.error, event.now);
}

function canStartTurn(state, event) {
  const resetAt = Date.parse(state.daily.resetAt);
  const limited = state.daily.remaining === 0 && Number.isFinite(resetAt) && resetAt > event.now;
  return matchesContext(state, event) && state.session.tag === "connected" && !activeTurnTags.includes(state.turn.tag) && !limited && !isCoolingDown(state, event.now);
}

function advanceTurn(state, tag, operation, extra = {}) {
  const key = state.operationSequence + 1;
  return { ...state, operationSequence: key, turn: {
    tag, error: null, ...extra,
    operation: { ...operation, kind: tag, key, epoch: state.context.epoch, dealId: state.context.dealId }
  } };
}

function rejectTurn(state, operation, error, permanent = false) {
  return { ...state, turn: {
    tag: "rejected", error,
    restoreComposer: { key: operation.key, epoch: operation.epoch, text: operation.envelope.composerText }
  }, messages: permanent
    ? state.messages.filter((item) => item.turnId !== operation.envelope.request.turnId)
    : state.messages.filter((item) => item !== operation.submittedMessage) };
}

function turnFailure(state, event) {
  const operation = selectTurnOperation(state);
  if (!matchesOperation(operation, event)) return state;
  const details = errorDetails(event.error);
  if (state.turn.tag === "resyncing") return sessionFailure(state, { error: details, daily: operation.daily, removeMessage: operation.submittedMessage, now: event.now }, "recovery");
  if (state.turn.tag === "sending" && !operation.renewalAttempted && ["AUTH_SESSION_EXPIRED", "SESSION_ENDED"].includes(details.code)) {
    return advanceTurn(state, "renewing", { ...operation, renewalAttempted: true, clearComposer: false });
  }
  if (isCredentialSupersededError(event.error) || isUnavailableError(event.error)) return sessionFailure(state, { error: details }, "access");
  if (["STALE_REVISION", "DAILY_LIMIT_REACHED"].includes(details.code)) {
    const body = event.error?.response?.data?.error || event.error?.response?.data || event.error?.error || event.error;
    const daily = details.code === "DAILY_LIMIT_REACHED"
      ? { remaining: 0, limit: typeof body.limit === "number" ? body.limit : null, resetAt: typeof body.resetAt === "string" ? body.resetAt : null }
      : null;
    const rejected = rejectTurn(state, operation, null);
    return advanceTurn(invalidateHistory(rejected), "resyncing", { reason: daily ? "daily" : "stale", daily, submittedMessage: operation.submittedMessage }, { restoreComposer: rejected.turn.restoreComposer });
  }
  if (details.code === "INPUT_TOO_LONG") return rejectTurn(state, operation, details);
  const permanent = typeof details.status === "number" && details.status >= 400 && details.status < 500
    && ![401, 403, 408, 425, 429].includes(details.status) && !(details.status === 404 && details.code === "SESSION_ENDED");
  if (permanent) return rejectTurn(state, operation, { ...details, retryable: false, message: "ההודעה נדחתה ולא נשלחה." }, true);
  const retryable = { ...state, turn: { tag: "retryable", envelope: operation.envelope, error: { ...details, retryable: true },
    ...(isAssistantRequestThrottle(details) ? { restoreComposer: { key: operation.key, epoch: operation.epoch, text: operation.envelope.composerText } } : {}) } };
  return recordThrottle(retryable, details, event.now);
}

export function assistantLifecycleReducer(state, event) {
  switch (event.type) {
    case "DEAL_CHANGED": {
      if (event.dealId === state.context.dealId) return state;
      const next = { ...createAssistantLifecycle(event.dealId, state.context.epoch + 1), operationSequence: state.operationSequence, snapshotVersion: state.snapshotVersion };
      // The limit belongs to the lawyer; the previous Deal's question does not.
      if (isCoolingDown(state, event.now)) return { ...next, throttle: state.throttle, session: { tag: event.dealId ? "throttled" : "noDeal" } };
      return next;
    }
    case "OPEN_REQUESTED": {
      if (isCoolingDown(state, event.now) || !event.visible || !state.context.dealId || activeTurnTags.includes(state.turn.tag) || state.session.tag === "opening") return state;
      const reason = event.reason || "initial";
      if (!["initial", "retry", "new"].includes(reason)) return state;
      if (reason === "initial" && state.session.tag !== "dormant" && !(state.session.tag === "blocked" && !state.session.value)) return state;
      if (reason === "retry" && !["blocked", "superseded", "throttled"].includes(state.session.tag)) return state;
      const base = reason === "new" ? { ...createAssistantLifecycle(state.context.dealId, state.context.epoch + 1), snapshotVersion: state.snapshotVersion } : invalidateHistory(state);
      const key = state.operationSequence + 1;
      return {
        ...base,
        throttle: null,
        operationSequence: key,
        session: { tag: "opening", value: reason === "new" ? null : state.session.value || null, operation: { key, epoch: base.context.epoch, dealId: base.context.dealId, reason } },
        turn: idleTurn()
      };
    }
    case "OPEN_SUCCEEDED":
      if (!matchesOperation(selectOpeningOperation(state), event)) return state;
      return validSession(event.session) ? replaceSnapshot(state, event) : sessionFailure(state, { ...event, error: invalidSessionError }, "open");
    case "OPEN_FAILED":
      return matchesOperation(selectOpeningOperation(state), event) ? sessionFailure(state, event, "open") : state;
    case "TURN_SUBMITTED": {
      if (!canStartTurn(state, event) || !event.envelope?.request?.turnId || !event.envelope.request.input || event.envelope.request.dealId !== state.context.dealId) return state;
      const base = { ...state, throttle: null, messages: event.message ? [...state.messages, event.message] : state.messages };
      return advanceTurn(base, "sending", { envelope: event.envelope, session: state.session.value, renewalAttempted: false, submittedMessage: event.message, clearComposer: true });
    }
    case "TURN_ID_FAILED":
      return canStartTurn(state, event) ? { ...state, turn: { tag: "rejected", error: { code: "TURN_ID_UNAVAILABLE", retryable: false } } } : state;
    case "TURN_RETRY_REQUESTED":
      return canStartTurn(state, event) && state.turn.tag === "retryable"
        ? advanceTurn({ ...state, throttle: null }, "sending", { envelope: state.turn.envelope, session: state.session.value, renewalAttempted: false, submittedMessage: null, clearComposer: false }, { error: isAssistantRequestThrottle(state.turn.error) ? null : state.turn.error }) : state;
    case "TURN_RENEWED": {
      const operation = selectTurnOperation(state);
      if (state.turn.tag !== "renewing" || !matchesOperation(operation, event)) return state;
      if (!validSession(event.session)) return turnFailure(state, { ...event, error: invalidSessionError });
      // A renewed credential is transient until the repeated turn completes (T:1732).
      return advanceTurn(state, "sending", { ...operation, session: event.session });
    }
    case "TURN_RESPONSE_RECEIVED": {
      const operation = selectTurnOperation(state);
      if (state.turn.tag !== "sending" || !matchesOperation(operation, event)) return state;
      if (event.result.conversationRef && event.result.conversationRef !== operation.session.conversationId) {
        return { ...state, turn: { tag: "retryable", envelope: operation.envelope, error: { code: "CONVERSATION_MISMATCH", correlationRef: event.result.correlationRef || null, retryable: true } } };
      }
      return advanceTurn(state, "applying", { ...operation, result: event.result });
    }
    case "TURN_FAILED":
      return turnFailure(state, event);
    case "TURN_RESYNCED": {
      const operation = selectTurnOperation(state);
      if (state.turn.tag !== "resyncing" || !matchesOperation(operation, event)) return state;
      if (!validSession(event.session)) return turnFailure(state, { ...event, error: invalidSessionError });
      return { ...replaceSnapshot(state, { ...event, daily: operation.daily }), turn: { tag: "rejected", error: operation.reason === "stale" ? { status: 409, retryable: false } : null } };
    }
    case "TURN_COMPLETED": {
      const operation = selectTurnOperation(state);
      if (state.turn.tag !== "applying" || !matchesOperation(operation, event)) return state;
      const result = operation.result;
      const defaultsConfirmed = typeof result.defaultsConfirmed === "boolean" ? result.defaultsConfirmed : null;
      const combined = defaultsConfirmed === null ? null : event.progress?.analysisReady === true && defaultsConfirmed;
      const wasReady = state.readiness.knownReadyForAnalysis;
      const becameReady = combined === true && (wasReady === false || (wasReady === null && (event.changedDraft || result.defaultsConfirmedThisTurn === true)));
      const showNotice = becameReady && !event.progress?.analysisDone;
      return { ...state,
        session: { tag: "connected", value: { ...operation.session, state: { revisionId: result.revisionId, sequence: result.sequence } } },
        turn: idleTurn(),
        readiness: defaultsConfirmed === null ? state.readiness : { knownDefaultsConfirmed: defaultsConfirmed, knownReadyForAnalysis: combined },
        messages: [...state.messages, event.reply, ...(showNotice ? [event.readyNotice] : [])],
        daily: { remaining: typeof result.dailyRemaining === "number" ? result.dailyRemaining : null, limit: typeof result.dailyLimit === "number" ? result.dailyLimit : null, resetAt: typeof result.dailyResetAt === "string" ? result.dailyResetAt : null }
      };
    }
    case "HISTORY_REQUESTED": {
      const history = state.history;
      if (isCoolingDown(state, event.now) || state.session.tag !== "connected" || state.turn.tag === "resyncing" || history.tag === "loading" || !history.hasMore || !history.beforeSeq) return state;
      // A failed page is retried explicitly; scrolling must not issue an automatic retry loop.
      if (history.tag === "failed" && !event.retry) return state;
      if (event.retry && history.tag !== "failed") return state;
      const key = state.operationSequence + 1;
      return { ...state, throttle: hasRetryableTurnThrottle(state) ? state.throttle : null, operationSequence: key, historyAppliedKey: null, history: {
        tag: "loading", epoch: history.epoch, hasMore: history.hasMore, beforeSeq: history.beforeSeq,
        operation: { key, epoch: state.context.epoch, historyEpoch: history.epoch, dealId: state.context.dealId, beforeSeq: history.beforeSeq }
      } };
    }
    case "HISTORY_SUCCEEDED": {
      const operation = selectHistoryOperation(state);
      if (!matchesOperation(operation, event) || operation.historyEpoch !== event.historyEpoch) return state;
      const knownSeqs = new Set(state.messages.map((item) => item.historySeq).filter((seq) => Number.isSafeInteger(seq)));
      const older = (event.messages || []).filter((item) => {
        if (knownSeqs.has(item.historySeq)) return false;
        knownSeqs.add(item.historySeq);
        return true;
      });
      return { ...state, messages: [...older, ...state.messages], history: idleHistory(state.history.epoch, event.history), historyAppliedKey: operation.key };
    }
    case "HISTORY_FAILED": {
      const operation = selectHistoryOperation(state);
      if (!matchesOperation(operation, event) || operation.historyEpoch !== event.historyEpoch) return state;
      return recordThrottle({ ...state, history: { tag: "failed", epoch: state.history.epoch, hasMore: state.history.hasMore, beforeSeq: operation.beforeSeq, error: event.error } }, event.error, event.now);
    }
    case "HISTORY_CANCELLED":
      return state.history.tag === "loading" ? invalidateHistory(state) : state;
    case "THROTTLE_CLEARED":
      return matchesContext(state, event) && state.session.tag !== "throttled" && !hasRetryableTurnThrottle(state) && !isCoolingDown(state, event.now) ? { ...state, throttle: null } : state;
    case "REQUEST_THROTTLED":
      return matchesContext(state, event) ? recordThrottle(state, event.error, event.now) : state;
    case "THROTTLE_EXPIRED":
      return matchesContext(state, event) && state.throttle?.until === event.until && event.now >= event.until
        ? { ...state, throttle: { ...state.throttle, expired: true } } : state;
    case "DAILY_RESET":
      return matchesContext(state, event) && state.daily.resetAt === event.resetAt && Date.parse(event.resetAt) <= event.now ? { ...state, daily: emptyDaily() } : state;
    case "MESSAGE_REVIEW_UPDATED":
      return matchesContext(state, event) ? { ...state, messages: state.messages.map((item) => item.reviewTurnId === event.reviewTurnId ? { ...item, ...event.review } : item) } : state;
    case "MESSAGE_REVIEW_SAVE_FAILED":
      return matchesContext(state, event) ? { ...state, messages: state.messages.map((item) => item.reviewTurnId === event.reviewTurnId
        ? { ...item, review: item.confirmedReview || null, reviewSaving: false, reviewFailed: event.reportThumbFailure } : item) } : state;
    case "LOCAL_HINT_SELECTED":
      return matchesContext(state, event) && state.session.tag === "connected" && !activeTurnTags.includes(state.turn.tag) ? { ...state, messages: [...state.messages, ...event.messages] } : state;
    case "MESSAGE_PRESENTATION_FINISHED":
    case "MESSAGE_PRESENTATIONS_CLEARED":
      return matchesContext(state, event) ? { ...state, messages: state.messages.map((item) => {
        if (!item.presentation || (event.type === "MESSAGE_PRESENTATION_FINISHED" && item !== event.message)) return item;
        const { presentation, ...withoutPresentation } = item;
        return withoutPresentation;
      }) } : state;
    default:
      return state;
  }
}
